import { useState, useEffect, useCallback } from "react";
import { IAircraftGroup } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatFunction, parseFunction } from "./helpers";

interface IAircraftGroupSelectorProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/**
 * Sepcialized wrapper for "built-in" oc aircraft group select field.
 * @param props -
 */
export const AircraftGroupSelectField = (props: IAircraftGroupSelectorProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  ///////////////////////////////////////
  // MANAGE AIRCRAFT GROUP LIST
  ///////////////////////////////////////
  const [aircraftGroups, setAircraftGroups] = useState<IAircraftGroup[]>([]);

  useEffect(() => {
    const getGroups = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Aircraft Groups found." };
      const response = await AmplifyAPIWrapper.Get<IAircraftGroup[]>("/aircraftGroup");
      if (Array.isArray(response)) setAircraftGroups(response);
    };
    getGroups();
  }, []);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, aircraftGroups, valueKey, isMulti),
    [aircraftGroups, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Aircraft Selector"}
      isMulti={isMulti}
      options={aircraftGroups}
      getOptionLabel={(option: IAircraftGroup) => `${option.title} (count:${option?.members?.length ?? "0"})`}
      getOptionValue={(option: IAircraftGroup) => option._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Aircraft Group${isMulti && "s"}`}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
